/**
 * Human labels + provenance for the opći-dio izjava variables. The backend
 * reports still-missing fields by raw variable name (see
 * ``permitpilot/docgen/opci_dio/statements.py``); this maps each to a Croatian
 * label and to *where* the user supplies it, so the "nedostaje" bar can route
 * the fix: office identity → Postavke (set once), project identity → Donesi.
 */

export type IzjavaFieldOrigin = "office" | "project";

interface IzjavaFieldMeta {
  label: string;
  origin: IzjavaFieldOrigin;
}

export const IZJAVA_FIELD_META: Record<string, IzjavaFieldMeta> = {
  // Office identity — set once in Postavke, reused across every project.
  ured_name: { label: "Ured (naziv)", origin: "office" },
  ured_addr: { label: "Ured (adresa)", origin: "office" },
  direktor_name: { label: "Direktor ureda", origin: "office" },
  gp_name_full: { label: "Glavni projektant", origin: "office" },
  gp_sig_name: { label: "Glavni projektant", origin: "office" },
  gp_sig_name_dot: { label: "Glavni projektant", origin: "office" },
  hka_klasa: { label: "HKA klasa", origin: "office" },
  hka_urbroj: { label: "HKA urbroj", origin: "office" },
  hka_broj: { label: "HKA broj", origin: "office" },
  hka_date: { label: "HKA datum", origin: "office" },
  // Project identity — supplied per project on Donesi.
  project_number: { label: "Broj projekta", origin: "project" },
  project_number_spaced: { label: "Broj projekta", origin: "project" },
  zop: { label: "Oznaka projekta (ZOP)", origin: "project" },
  investitor_name: { label: "Investitor", origin: "project" },
  investitor_address: { label: "Adresa investitora", origin: "project" },
  investitor_oib: { label: "OIB investitora", origin: "project" },
  signer_name: { label: "Potpisnik imenovanja", origin: "project" },
  zahvat: { label: "Naziv građevine / zahvata", origin: "project" },
  cestica_line: { label: "Katastarska čestica", origin: "project" },
  place_date: { label: "Mjesto i datum", origin: "project" },
  plan_lines: { label: "Popis prostornih planova", origin: "project" },
  pu_klasa: { label: "Posebni uvjeti — KLASA", origin: "project" },
  pu_urbroj: { label: "Posebni uvjeti — URBROJ", origin: "project" },
  pu_mjesto_datum: { label: "Posebni uvjeti — mjesto i datum", origin: "project" },
};

export function izjavaFieldLabel(key: string): string {
  return IZJAVA_FIELD_META[key]?.label ?? key;
}

export function izjavaFieldOrigin(key: string): IzjavaFieldOrigin {
  // Office identity is the closed set above; anything unknown is project-level.
  return IZJAVA_FIELD_META[key]?.origin ?? "project";
}

/**
 * Group a section's raw ``missing`` keys into deduped, human-labelled buckets
 * by where they're supplied. Order within a bucket follows first appearance.
 */
export function groupMissing(missing: string[]): {
  office: string[];
  project: string[];
} {
  const office: string[] = [];
  const project: string[] = [];
  for (const key of missing) {
    const label = izjavaFieldLabel(key);
    const bucket = izjavaFieldOrigin(key) === "office" ? office : project;
    if (!bucket.includes(label)) bucket.push(label);
  }
  return { office, project };
}
